import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Globe, Shield, Award, Target, Users } from 'lucide-react';
import { Reveal } from '@/components/Reveal';

const values = [
  { icon: Shield, title: 'Integrity & Confidentiality', desc: 'Every engagement operates under comprehensive confidentiality obligations. Client information, processes, and relationships are never disclosed or leveraged beyond the specific engagement.' },
  { icon: Award, title: 'Excellence in Execution', desc: 'We hold ourselves to high standards in everything we deliver. Average execution is not acceptable — every output should reflect the quality standards our clients deserve.' },
  { icon: Globe, title: 'International Professionalism', desc: 'We understand the communication standards, professional expectations, and business cultures of our international clients and operate accordingly at all times.' },
  { icon: Target, title: 'Long-Term Partnership', desc: 'We think in terms of long-term relationships, not transactions. Our goal is to become a trusted operational partner that clients rely on for years, not months.' },
];

const stats = [
  { num: '2023', label: 'Founded' },
  { num: '10+', label: 'Core Services' },
  { num: '6', label: 'Global Regions' },
  { num: '10', label: 'Industry Verticals' },
];

const differencePoints = [
  'Process-driven, not improvised operations',
  'Confidentiality as a non-negotiable standard',
  'International communication professionalism',
  'Long-term partnership thinking',
  'Quality over cost in every decision',
  'Accountability for every commitment made',
];

export default function About() {
  useEffect(() => { document.title = 'About NKP VENTURES — Empowering Businesses Since 2023'; }, []);

  return (
    <div>
      {/* ── Hero ── */}
      <section
        className="relative pt-32 pb-24 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0A1628 0%, #153E5C 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.016) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-2.5 mb-8">
              <span className="w-5 h-px bg-[#D4AF37]" />
              <span className="overline-label text-[#D4AF37]">About NKP VENTURES</span>
            </div>
            <h1 className="font-serif font-bold text-white leading-[1.05] mb-8" style={{ fontSize: 'clamp(2.6rem, 6vw, 4.2rem)' }}>
              A premium operations partner
              <br />
              <span style={{ color: '#D4AF37' }}>for international businesses.</span>
            </h1>
            <p className="text-white/55 text-lg font-sans leading-relaxed max-w-2xl">
              PKRUPARTH ENTERPRISE PVT. LTD. (NKP VENTURES) is built on a simple belief: that businesses deserve operational support they can genuinely trust.
            </p>
          </motion.div>

          {/* Stats strip inside hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-8 mt-16 pt-10 border-t border-white/10"
          >
            {stats.map(s => (
              <div key={s.label}>
                <div className="font-mono text-3xl font-bold text-white">{s.num}</div>
                <div className="text-[11px] text-white/40 font-sans tracking-widest uppercase mt-1">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Mission ── */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <Reveal>
              <p className="overline-label text-[#0E5A57] mb-4">Our Mission</p>
              <h2 className="font-serif text-4xl font-bold text-[#153E5C] mb-6 leading-tight">
                Empowering businesses through dependable execution.
              </h2>
              <p className="text-[#374151] text-base leading-relaxed font-sans mb-6">
                NKP VENTURES was founded in 2023 with a clear mission: to provide international businesses with operational support that is reliable, professional, and genuinely valuable — not just cheaper.
              </p>
              <p className="text-[#374151] text-base leading-relaxed font-sans mb-10">
                Too many businesses settle for operational support that is inconsistent, poorly communicated, and ultimately frustrating to manage. We built NKP VENTURES as the alternative — a partner that takes operational responsibility seriously and delivers with the professional standards that international businesses expect.
              </p>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 text-[#0E5A57] font-semibold text-sm hover:gap-3 transition-all duration-200"
              >
                Explore our services <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="bg-[#F7FAFC] border border-[#E5E7EB] rounded-2xl overflow-hidden">
                <div className="px-8 py-6 border-b border-[#E5E7EB]">
                  <p className="overline-label text-[#D4AF37]">Company Profile</p>
                </div>
                <div className="p-8 space-y-5">
                  {[
                    { label: 'Legal Name', value: 'PKRUPARTH ENTERPRISE PVT. LTD.' },
                    { label: 'Founded', value: '2023' },
                    { label: 'Headquarters', value: 'Ahmedabad, Gujarat, India' },
                    { label: 'Service Regions', value: 'UK · US · Canada · Europe · Australia · NZ' },
                    { label: 'Service Lines', value: '10 core BPO services' },
                    { label: 'Industries Served', value: '10 industry verticals' },
                  ].map((item) => (
                    <div key={item.label} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-6 py-1">
                      <span className="font-mono text-[10px] text-[#9CA3AF] uppercase tracking-wider min-w-[170px] pt-0.5">{item.label}</span>
                      <span className="text-sm font-medium text-[#1F2937] font-sans">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Values ── */}
      <section className="py-28 bg-[#F7FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <p className="overline-label text-[#0E5A57] mb-4">Our Philosophy</p>
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-[#153E5C] mb-4 max-w-2xl leading-tight">
              The values that guide every engagement.
            </h2>
            <p className="text-[#6B7280] max-w-2xl leading-relaxed font-sans mb-16">
              These are not aspirational statements. They are the operating standards we hold ourselves to every day.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title} delay={i * 0.08}>
                  <div className="group p-8 bg-white border border-[#E5E7EB] rounded-2xl hover:border-[#0E5A57]/20 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
                    <div className="w-12 h-12 bg-teal-50 border border-teal-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#0E5A57] group-hover:border-[#0E5A57] transition-all duration-300">
                      <Icon className="w-6 h-6 text-[#0E5A57] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="font-serif text-xl font-bold text-[#153E5C] mb-3">{v.title}</h3>
                    <p className="text-sm text-[#6B7280] leading-relaxed font-sans">{v.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Positioning ── Dark section */}
      <section className="py-28" style={{ background: '#0D1F30' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-6 h-px bg-[#D4AF37]" />
                <p className="overline-label text-[#D4AF37]">Our Positioning</p>
              </div>
              <h2 className="font-serif text-3xl lg:text-4xl font-bold text-white mb-6 leading-tight">
                Premium by standard,<br />not by price.
              </h2>
              <p className="text-white/55 text-base font-sans leading-relaxed mb-6">
                NKP VENTURES is not positioned as the cheapest outsourcing option. We are positioned as the most reliable, professional, and trustworthy one. The businesses that choose us are looking for an operational partner they can count on — not simply a cost reduction exercise.
              </p>
              <p className="text-white/55 text-base font-sans leading-relaxed">
                This positioning demands that we hold ourselves to standards most outsourcing providers do not commit to: professional communication, documented processes, quality controls, and consistent delivery.
              </p>
            </Reveal>

            <Reveal delay={0.12}>
              <div className="space-y-3">
                {differencePoints.map((point, i) => (
                  <motion.div
                    key={point}
                    initial={{ opacity: 0, x: 16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.06, duration: 0.5 }}
                    className="flex items-center gap-4 p-4 border border-white/8 rounded-xl hover:border-white/16 hover:bg-white/3 transition-all duration-200"
                  >
                    <div className="w-1 h-6 bg-gradient-to-b from-[#0E5A57] to-[#D4AF37] rounded-full flex-shrink-0" />
                    <span className="text-white/75 text-sm font-sans">{point}</span>
                  </motion.div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Leadership Placeholder ── */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <p className="overline-label text-[#0E5A57] mb-4">Leadership & Team</p>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
              <h2 className="font-serif text-4xl font-bold text-[#153E5C] leading-tight max-w-lg">
                The people behind<br />NKP VENTURES.
              </h2>
              <p className="text-sm text-[#6B7280] max-w-xs font-sans leading-relaxed lg:text-right">
                Leadership profiles will be published shortly. Our team brings expertise across operations, client management, and international business.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[1, 2, 3].map(i => (
                <div key={i} className="group p-8 bg-[#F7FAFC] border border-[#E5E7EB] border-dashed rounded-2xl flex flex-col items-center text-center">
                  <div className="w-20 h-20 bg-[#E5E7EB] rounded-full flex items-center justify-center mb-5">
                    <Users className="w-8 h-8 text-[#D1D5DB]" />
                  </div>
                  <div className="w-28 h-3 bg-[#E5E7EB] rounded mb-2" />
                  <div className="w-20 h-2.5 bg-[#F3F4F6] rounded mb-4" />
                  <p className="text-xs text-[#9CA3AF] font-sans">Profile coming soon</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-28 bg-[#0E5A57]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="overline-label text-teal-300 mb-4">Work With Us</p>
                <h2 className="font-serif text-4xl font-bold text-white mb-4 leading-tight">
                  Ready to build a better<br />operational partnership?
                </h2>
                <p className="text-white/60 font-sans leading-relaxed max-w-md">
                  Get in touch to discuss how NKP VENTURES can support your business operations.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
                <Link to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E8CA5A] text-[#153E5C] font-bold px-9 py-4 rounded-full transition-all duration-200 group">
                  Schedule a Consultation
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
                </Link>
                <Link to="/services"
                  className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white/50 text-white font-medium px-9 py-4 rounded-full transition-all duration-200 text-sm">
                  Explore Services
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
